import { Checkbox, Label, Text } from "@gravity-ui/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { ProductSourceDtoSourceEnum } from "definitions/product-storage";
import { ProductSelectionDto } from "definitions/product-storage/models/product-selection-dto";
import { PRODUCTS_SAMPLE_LIMIT } from "src/env";
import { T } from "src/i18n";

import { ProductTableData } from "../../store/fetchers";
import {
  ImageCell,
  NameCell,
  PriceCell,
  PriceRange,
  SourcesCell,
} from "./components";

export const getColumns = (
  selectedIds: Set<number>,
  products: ProductTableData[],
  productSample: ProductSelectionDto | null,
  onChangeSelect?: (ids: number[]) => void,
): ColumnDef<ProductTableData>[] => [
  {
    accessorKey: "select",
    header: () => {
      const currentIds = products?.map((product) => product.id) || [];
      const isAllSelected =
        currentIds.length > 0 && currentIds.every((id) => selectedIds.has(id));

      const hasSelectedProductSample = !!productSample;

      const isDisabled =
        !hasSelectedProductSample ||
        (selectedIds && selectedIds.size >= PRODUCTS_SAMPLE_LIMIT);

      return (
        <div
          onClick={(e) => {
            e.stopPropagation();

            if (!onChangeSelect || isDisabled) return;

            const newSelectedIds = new Set(selectedIds);

            if (isAllSelected) {
              currentIds.forEach((id) => newSelectedIds.delete(id));
            } else {
              currentIds.forEach((id) => newSelectedIds.add(id));
            }

            onChangeSelect(Array.from(newSelectedIds));
          }}
        >
          <Checkbox
            size="l"
            checked={isAllSelected}
            disabled={!isAllSelected && isDisabled}
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const product = row.original;
      const isSelected = selectedIds?.has(product?.id) || false;
      const hasSelectedProductSample = !!productSample;
      const isDisabled =
        !hasSelectedProductSample ||
        (selectedIds &&
          selectedIds.size >= PRODUCTS_SAMPLE_LIMIT &&
          !selectedIds.has(product.id));

      return (
        <div
          onClick={(e) => {
            e.stopPropagation();

            if (!onChangeSelect || isDisabled) return;

            const newSelectedIds = new Set(selectedIds);

            if (newSelectedIds.has(product.id)) {
              newSelectedIds.delete(product.id);
            } else {
              newSelectedIds.add(product.id);
            }

            onChangeSelect(Array.from(newSelectedIds));
          }}
        >
          <Checkbox size="l" checked={isSelected} disabled={isDisabled} />
        </div>
      );
    },
    size: 40,
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "logoUrl",
    cell: ({ getValue }) => <ImageCell image={getValue<string>() || ""} />,
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="product_page.product_list.table.header.logoUrl"
          defaultValue="Фото"
        />
      </Text>
    ),
    size: 80,
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "name",
    cell: ({ getValue }) => <NameCell name={getValue<string>() || ""} />,
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="product_page.product_list.table.header.name"
          defaultValue="Название"
        />
      </Text>
    ),
    size: 250,
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "article",
    cell: ({ getValue }) => (
      <span onClick={(e) => e.stopPropagation()}>
        <Label copyText={getValue<string>() || "-"} type="copy">
          {getValue<string>() || "-"}
        </Label>
      </span>
    ),
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="product_page.product_list.table.header.article"
          defaultValue="Артикул"
        />
      </Text>
    ),
    size: 120,
    enableSorting: false,
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "sources",
    cell: ({ getValue }) => (
      <SourcesCell sources={getValue<ProductSourceDtoSourceEnum[]>() || []} />
    ),
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="product_page.product_list.table.header.sources"
          defaultValue="Источники"
        />
      </Text>
    ),
    size: 120,
    enableSorting: false,
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "priceRange",
    cell: ({ getValue }) => (
      <PriceCell priceRange={getValue<PriceRange>() || {}} />
    ),
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="product_page.product_list.table.header.priceRange"
          defaultValue="Диапазон цен"
        />
      </Text>
    ),
    size: 120,
    footer: (props) => props.column.id,
  },
];
